

export type Integration = {
  integrationType: string;
  integrationDetails: Map<string, string>;
};

export type Organization = {
  id: number;

  createdAt?: string;
  active: boolean;

  approvedBy: string;
  approvedAt?: string;

  ownerId: number;
  iftaOnly: boolean;
  deviceOnly: boolean;
  eldOnly: boolean;
  dayOfTheWeekDriversArePaid: number;

  organizationName: string;

  companyLogoUrl: string;
  companyLogoText: string;
  invoiceBottomText: string;
  footerCompanyNameText: string;
  organizationTin: string;

  integrations: Integration[];
};

export type UserForEdit = {
  email: string;
  name: string;
  phoneNumber: string;
  password: string;
  newPassword: string;
};

export type OrganizationForSession = {
  id: number;
  ownerId: number;

  organizationName: string;
};

export type UserForSession = {
  id: number;
  name: string;
  email: string;
  organizationIds: number[];
  organizationNames: string[];
};

export type GlAccount = {
  id: number;

  createdAt?: string;
  deletedAt?: string;
  organizationId: number;
  companyId: number;
  baseType: number;
  parentId: number;
  accountNum: string;
  accountName: string;
  description: string;
};

export type Address = {
  attention: string;
  street1: string;
  street2: string;
  city: string;
  state: string;
  zip: string;
};

export type PayRate = {
  payRateType: number;
  rateName: string;
  comment: string;
  rate: number;
};

export type TruckDriverFile = {
  createdAt?: string;
  uploaderUserId: number;
  fileUrl: string;
  originalFileName: string;
  fileType: string;
  comment: string;
  expiresAt?: string;
  reminder: number;
};

export type Driver = {
  id: number;
  createdAt?: string;
  active: boolean;

  startDate?: string;
  endDate?: string;

  organizationId: number;
  driverNumber: string;
  fuelCardNumber: string;
  assignedTruckNumber: string;
  assignedTruckId: number;
  companyDriver: boolean;
  businessPartnerId: number;
  companyName: string;
  mcFfNumber: string;
  feinSsnNumber: string;
  usDotNumber: string;

  firstName: string;
  middleName: string;
  lastName: string;
  dateOfBirth?: string;
  ssnNumber: string;
  feinNumber: string;
  contactPhoneNumber: string;
  contactEmail: string;
  comment: string;
  payRates: PayRate[];

  driverAddress: Address;

  files: TruckDriverFile[];
};

export type TruckDriverFileWithStatus = TruckDriverFile & {
  status?: 'warning' | 'error';
};

export type DriverWithExpirations = Driver & {
  expirations: Map<string, TruckDriverFileWithStatus>;
};

export type TruckWithExpirations = Truck & {
  expirations: Map<string, TruckDriverFileWithStatus>;
};
export type EquipmentWithExpirations = Equipment & {
  expirations: Map<string, TruckDriverFileWithStatus>;
};

export type BusinessPartner = {
  id: number;
  createdAt?: string;
  active: boolean;
  organizationId: number;
  driverId: number;
  companyName: string;

  address: Address;

  mcFfNumber: string;
  feinSsnNumber: string;
  usDotNumber: string;
  comment: string;

  isCustomer: boolean;
  isTruckOwner: boolean;
};

export type FuelCharge = {
  id: number;
  createdAt?: string;
  deletedAt?: string;
  organizationId: number;
  transactionDate: string;
  amount: number;
  accountNumber: string;
  reason: string;
  driverId: number;
  driverFirstName: string;
  driverMiddleName: string;
  driverLastName: string;
  driverNumber: string;
  locationName: string;
  location: Address;

  gallons: number;
  fuel: number;
  advance: number;
  misc: number;
};

export type Credit = {
  id: number;
  createdAt?: string;
  deletedAt?: string;
  organizationId: number;
  transactionDate: string;
  amount: number;
  reason: string;
  driverId: number;
  driverFirstName: string;
  driverMiddleName: string;
  driverLastName: string;
};

export type TollCharge = {
  id: number;
  createdAt?: string;
  deletedAt?: string;
  organizationId: number;
  transactionDate: string;
  amount: number;
  accountNumber: string;
  driverId: number;
  driverFirstName: string;
  driverMiddleName: string;
  driverLastName: string;
  location: Address;
};

export type Deduction = {
  id: number;
  createdAt?: string;
  deletedAt?: string;
  organizationId: number;
  scheduledDeductionId: number;
  transactionDate: string;
  amount: number;
  reason: string;
  driverId: number;
  driverFirstName: string;
  driverMiddleName: string;
  driverLastName: string;
};

export type ScheduledDeduction = {
  id: number;
  createdAt?: string;
  deletedAt?: string;
  organizationId: number;
  recurrenceType: number;
  onMonth: number;
  onDay: number;
  startDate: string;
  endDate: string;
  lastGenerated: string;
  amount: number;
  reason: string;
  driverId: number;
  driverFirstName: string;
  driverMiddleName: string;
  driverLastName: string;
};

export type LoadStop = {
  loadStopType: number;
  reason: string;
  zip: string;
};
export type LoadPayRate = {
  loadPayRateType: number;

  truckId: number;
  truckNumber: string;
  companyName: string;

  driverId: number;
  driverFirstName: string;
  driverMiddleName: string;
  driverLastName: string;
  payRateType: number;

  rateName: string;
  comment: string;
  rate: number;
  amount: number;
};

export type Load = {
  id: number;

  createdAt?: string;
  deletedAt?: string;
  createdByUserId: number;
  loadStatus: number;
  organizationId: number;
  firstDriverId: number;
  firstDriverFirstName: string;
  firstDriverMiddleName: string;
  firstDriverLastName: string;
  truckNumber: string;
  truckId: number;
  truckCompanyName: string;
  companyTruck: boolean;
  secondDriverId: number;
  secondDriverFirstName: string;
  secondDriverMiddleName: string;
  secondDriverLastName: string;
  businessPartnerId: number;
  businessPartnerName: string;
  businessPartnerAddress: Address;
  loadNumber: number;
  customerLoadNumber: string;
  loadDate: string;
  netDays: number;
  dueDate: string;
  loadAmount: number;
  freightAmount: number;
  lumperAmount: number;
  totalDistance: number;
  totalDriverPay: number;
  totalTruckPay: number;
  totalPay: number;
  fromAddress: Address;
  toAddress: Address;
  loadStops: LoadStop[];
  loadPayRates: LoadPayRate[];
};

export type AmtByBusinessPartner = {
  businessPartnerName: string;
  amount: number;
};

export type DashboardData = {
  amtByBusinessPartner: AmtByBusinessPartner[];
  companyDrivers: number;
  nonCompanyDrivers: number;
  loadsLast28Days: number;
  loadsAmtLast28Days: number;
  chargesLast28Days: number;
  chargesAmtLast28Days: number;
  deductionsLast28Days: number;
  deductionsAmtLast28Days: number;
  driversLast28Days: number;
  driverPayLast28Days: number;

  loadsAmtPerWeek: number[];
  chargesAmtPerWeek: number[];
  deductionsAmtPerWeek: number[];
  driversAmtPerWeek: number[];
};

export type Truck = {
  id: number;
  createdAt?: string;

  startDate?: string;
  endDate?: string;

  active: boolean;
  organizationId: number;
  truckNumber: string;
  tollNumber: string;
  registration: string;

  barcode: string;

  vin: string;
  make: string;
  model: string;
  color: string;
  yearMade: string;

  preMaintenanceNotes: string;

  companyTruck: boolean;
  companyName: string;
  businessPartnerId: number;

  miles: number;
  lastLat: number;
  lastLong: number;
  lastAddress: string;

  comment: string;
  payRates: PayRate[];

  files: TruckDriverFile[];
};

export type AttachedFile = {
  id: number;
  createdAt?: string;
  deletedAt?: string;
  organizationId: number;
  uploaderUserId: number;
  fileUrl: string;
  originalFileName: string;
  attachedToEntity: string;
  attachedToId: number;
};

export type StatementLine = {
  description: string;
  refrenceNumber: number;
  refrenceType: number;
  refrenceId: number;
  refrenceDate: string;

  // fuel values

  gallons: number;
  fuel: number;
  misc: number;
  advance: number;

  // load values
  freightValue: number;
  mileage: number;

  amount: number;
};

export type Statement = {
  id: number;

  statementType: number;

  createdAt?: string;
  deletedAt?: string;
  organizationId: number;

  statementStatus: number;
  statementDate: string;

  driverId: number;
  driverFirstName: string;
  driverMiddleName: string;
  driverLastName: string;
  driverAddress: Address;

  companyDriver: boolean;

  businessPartnerId: number;
  companyName: string;

  totalAmount: number;

  statementLines: StatementLine[];
};

export type RevenueReportLine = {
  description: string;
  refrenceNumber: number;
  refrenceType: number;
  refrenceId: number;
  refrenceDate: string;

  // fuel values

  gallons: number;
  fuel: number;
  misc: number;
  advance: number;

  // load values
  freightValue: number;
  mileage: number;

  amount: number;
};

export type RevenueReport = {
  id: number;

  revenueReportType: number;

  createdAt?: string;
  deletedAt?: string;
  organizationId: number;

  revenueReportStatus: number;
  periodFrom: string;
  periodTo: string;

  driverId: number;
  driverFirstName: string;
  driverMiddleName: string;
  driverLastName: string;
  driverAddress: Address;

  companyDriver: boolean;
  companyName: string;

  businessPartnerId: number;

  totalAmount: number;

  revenueReportLines: RevenueReportLine[];
};

export type ValidationMessage = {
  field: string;
  message: string;
};

export type OrganizationUserForList = {
  id: number;
  createdAt?: string;
  active: boolean;
  organizationId: number;
  userId: number;
  isDispatcher: boolean;
  email: string;
  name: string;
  phone: string;
};

export type OrganizationUser = {
  id: number;
  name: string;
  email: string;
  createdAt?: string;
  active: boolean;
  organizationId: number;
  userId: number;

  accounting: boolean;
  safety: boolean;
  dispatch: boolean;
  shop: boolean;
  fleet: boolean;
  taxes: boolean;

  isDispatcher: boolean;

  viewDebts: boolean;
  updateDebts: boolean;

  viewBusinessPartners: boolean;
  updateBusinessPartners: boolean;
  viewCredits: boolean;
  updateCredits: boolean;
  viewDeductions: boolean;
  updateDeductions: boolean;
  viewDrivers: boolean;
  updateDrivers: boolean;
  viewFuelCharges: boolean;
  updateFuelCharges: boolean;
  updateGetStarteds: boolean;
  viewLoads: boolean;
  updateLoads: boolean;
  viewOrganizationUsers: boolean;
  updateOrganizationUsers: boolean;
  viewOrganizations: boolean;
  updateOrganizations: boolean;
  viewRevenueReports: boolean;
  updateRevenueReports: boolean;
  viewScheduledDeductions: boolean;
  updateScheduledDeductions: boolean;
  viewStatements: boolean;
  updateStatements: boolean;
  viewTollCharges: boolean;
  updateTollCharges: boolean;
  viewTrucks: boolean;
  updateTrucks: boolean;
  viewEquipment: boolean;
  updateEquipment: boolean;
  viewUsers: boolean;
  updateUsers: boolean;
};

export type IftaFuelLine = {
  importedFromSystem: string;
  importedFromFile: string;

  truckNumber: string;
  state: string;
  gallons: number;
  amount: number;
};

export type IftaFilingLine = {
  truckNumber: string;
  milesPerGallon: number;
  stateLines: IftaStateLine[];
  totalTax: number;
  totalMiles: number;
  totalTaxableMiles: number;
  totalFuelAmount: number;

  totalTaxableGallons: number;
  totalTaxPaidGallons: number;
  totalNetTaxableGallons: number;
};
export type IftaStateLine = {
  state: string;
  tax: number;
  taxableMiles: number;
  totalMiles: number;
  fuelAmount: number;

  taxableGallons: number;
  taxPaidGallons: number;
  netTaxableGallons: number;
  overcharge: boolean;
};

export type IftaMileLine = {
  importedFromSystem: string;
  importedFromFile: string;

  truckNumber: string;
  state: string;
  taxableMiles: number;
  totalMiles: number;
};

export type IftaUploadedFile = {
  fileName: string;
  fileUrl: string;
  fileId: string;
};

export type IftaFiling = {
  id: number;

  createdAt?: string;
  deletedAt?: string;
  organizationId: number;
  title: string;

  fuelLines: IftaFuelLine[];
  mileLines: IftaMileLine[];
  filingLines: IftaFilingLine[];
  totalFilingLine: IftaFilingLine;
  byTruck: boolean;
  fuelFiles: IftaUploadedFile[];
  mileFiles: IftaUploadedFile[];
  roundPerTruckState: boolean;
  selectedQuarter: string;
  selectedState: string;
};

export type Equipment = {
  id: number;
  createdAt?: string;
  active: boolean;

  startDate?: string;
  endDate?: string;

  organizationId: number;
  equipmentNumber: string;
  registration: string;

  barcode: string;

  companyEquipment: boolean;
  companyName: string;
  businessPartnerId: number;

  vin: string;
  make: string;
  model: string;
  color: string;
  yearMade: string;

  preMaintenanceNotes: string;

  assignedTruckNumber: string;
  assignedTruckId: number;

  comment: string;
  files: TruckDriverFile[];
};

export type Reminder = {
  intensity: 'error' | 'warning' | 'success';
  entityType: string;
  entityId: number;
  entityPreview: string;
  reminderType: string;

  comment: string;

  expiresAt?: string;
  reminder: number;

  // only set when reminder type is File
  fileType: string;
  fileUrl: string;
  originalFileName: string;
};

export type AccidentFile = {
  createdAt?: string;
  fileUrl: string;
  originalFileName: string;
  comment: string;
};

export type Accident = {
  id: number;
  createdAt?: string;
  deletedAt?: string;

  estimatedAmount: number;
  deductable: number;

  claimNumber: string;

  accidentStatus: number;
  // working on it
  // in process
  // done
  accidentProgressStatus: number;

  amount: number;

  organizationId: number;

  insuranceCompany: string;

  accidentDate: string;
  accidentTime: string;
  driverId: number;

  hasPoliceReport: boolean;
  policeReportInfo: string;

  driverFirstName: string;
  driverMiddleName: string;
  driverLastName: string;

  isTruck: boolean;

  truckEquipmentNumber: string;
  truckEquipmentId: number;

  notes: string;

  location: Address;
  files: AccidentFile[];
};

export type Debt = {
  id: number;
  createdAt?: string;
  deletedAt?: string;
  organizationId: number;

  vin: string;
  make: string;
  model: string;
  color: string;
  yearMade: string;
  miles: number;

  lender: string;
  startBalance: number;
  ratePcnt: number;
  termMonths: number;
  monthlyPayment: number;
  firstPayment: string;
  notes: string;
  lastPayment: string;
  marketValue: number;
};

export type MaintenanceItemQualifier = {
  maintenanceItemQualifierType: number;

  everyType: number; // day month count
  everyCount: number;

  odometerFrom: number;
  odometerTo: number;
};

export type MaintenanceItem = {
  id: number;
  needsReminder: boolean;
  createdAt?: string;
  deletedAt?: string;
  organizationId: number;

  name: string;
  description: string;
  qualifiers: MaintenanceItemQualifier[];

  appliesToType: "all" | "groups" | "devices";
  appliesToIds: number[]; // empty when type is all
};

export type MaintenanceGroup = {
  id: number;
  createdAt?: string;
  deletedAt?: string;

  organizationId: number;

  name: string;
  description: string;

  maintenanceGroupMechanicIds: number[]; // empty array means all mechanics can work on this group
  maintenanceGroupDeviceIds: number[];
}

export type MaintenanceItemPerformed = {
  item: MaintenanceItem;
  passed: boolean;
  lastDoneDate: string;
  lastDoneMiles: number;
  nextDate: string;
  nextMiles: number;
}

export type MaintenanceMechanic = {
  id: number;

  createdAt?: string;
  active: boolean;
  organizationId: number;

  mechanicName: string;

  sessionGeneratedAt?: string;

  pin: string;
  canPerformUnassignedMaintenance: boolean;
};


export type MaintenanceWorkOrder = {
  id: number;
  createdAt?: string;
  deletedAt?: string;

  maintenanceDate: string;
  organizationId: number;

  maintenanceMechanicName: string;
  maintenanceMechanicId: number;

  maintenanceStatus: number; // 0 scheduled, 1 done, 2 cancelled

  deviceId: number;
  deviceType: string;
  vin: string; // only if this is an ELD truck

  odometer: number;
  maintenanceItemsPerformed: MaintenanceItemPerformed[];
  preMaintenanceNotes: string;
  postMaintenanceNotes: string;

  attachments: MaintenanceWorkOrderAttachment[]
};

type MaintenanceWorkOrderAttachment = {
  createdAt?: string;
  title: string;
  url: string;
  addedBy: string;
}

export type MaintenanceReminderItem = {
  intensity: 'error' | 'warning' | 'success';
  maintenanceItem: MaintenanceItem;
  dueIn: string;
};

export type MaintenanceReminder = {
  deviceId: number;
  deviceType: string;
  vin: string; // only if this is an ELD truck
  odometer: number;

  preMaintenanceNotes: string;

  intensity: 'error' | 'warning' | 'success' | 'none';
  reason: string;
  items: MaintenanceReminderItem[];
};

export type DeviceHistoryLineWithCityName = {
  recordedAt: string;
  posLat: string;
  posLong: string;
  odometer?: number;
  engineHours?: number;
  fuelLevel?: number;
  battery?: number;
  direction?: number;
  velocity?: number;
  lineOrigin?: number;
  cityName: string;
  nearCamera?: boolean;
}

export type CameraLocation = {
  lat: number;
  lng: number;
};

export type Device = {
  id: number;

  createdAt?: string;
  active: boolean;
  organizationId: number;

  deviceUuid: string;
  lastPosLat: string;
  lastPosLong: string;
  lastOdometer: number;

  direction?: number;
  velocity?: number;

  origin?: number;

  lastPing?: string;
  deviceType: string;
  camera?: CameraConfig;
  battery: number;
  externalIdentifier: string;
  lastDormancyReported?: string;
};

export type TpmsVehicleType = 'truck' | 'trailer';

export type TpmsSensor = {
  sensorId: string;
  reportedPosition: number;
  wheelSlot: number | null;
  pressureBar: number;
  temperatureC: number;
  batteryVolts: number;
  status: number;
  lastReportedAt: string;
};

export type TpmsDevice = {
  id: number;
  createdAt: string;
  organizationId: number;
  vehicleType: TpmsVehicleType;
  sensors: TpmsSensor[];
  lastReportedAt: string;
  deviceName: string;
};

export type CameraNotification = {
  id: number;

  createdAt?: string;
  deletedAt?: string;
  organizationId: number;

  eventDate: string;
  eventDateTime?: string;
  durationSeconds: number;

  location: string;

  videoUrl: string;
  previewUrl: string;
  videoStatus: number;
  videoError: string;
  videoUploadedAt?: string;

  reason: string;
  requestedBy: string;
  eventCode: string;
  requestSerial: string;

  imei: string;
  iccid: string;
  type: number;

  deviceId: number;

  latitude?: number;
  longitude?: number;
  speed?: number;

  eldVehicleId: number;
  eldVehicleUnitNumber: string;
  eldVehicleVin: string;

  eldDriverId: number;
  eldDriverUsername: string;
  eldDriverFirstName: string;
  eldDriverLastName: string;
}

export const CAMERA_VIDEO_STATUS_NONE = 0;
export const CAMERA_VIDEO_STATUS_PENDING = 1;
export const CAMERA_VIDEO_STATUS_AVAILABLE = 2;
export const CAMERA_VIDEO_STATUS_FAILED = 3;
export const CAMERA_VIDEO_STATUS_QUEUED = 4;

export type Camera = {
  id: number;

  createdAt?: string;
  deletedAt?: string;
  organizationId: number;

  imei: string;
  iccid: string;
  type: number;

  uploadUuid: string;
  lastPingAt?: string;
};


export type CameraConfig = {
  imei: string;
  iccid: string;
  type: number;
}

export type DeviceEldDriver = {
  id: number;
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  connected: boolean;
  eldVehicleUnitNumber: string;
}

export type DeviceWithMapInfo = Device & {
  location: string;
}

export type DeviceWithLastAssetNotification = Device & {
  assetNotification?: EldAssetNotification
};

export type RentalCustomer = {
  id: number;

  createdAt?: string;
  active: boolean;
  organizationId: number;

  username: string;
  password: string;

  companyName: string;
  firstName: string;
  lastName: string;

  address: Address;
  phone: string;
  email: string;
  comment: string;
  dotNumber: string;
  mcNumber: string;
  feinNumber: string;
}

export type RentalTrailer = {
  id: number;

  createdAt?: string;
  active: boolean;
  organizationId: number;

  trailerUnitNumber: string;
  make: string;
  model: string;
  year: string;
  vin: string;
  licensePlateState: string;
  licensePlate: string;
  // key into RENTAL_TRAILER_TYPES; required on save, '' only on legacy rows
  trailerType: string;
  // direct link to the GPS device, possibly owned by another organization
  // (ct_rental_trailers integration); 0 = linked by unit number only
  deviceId: number;

  registrationFile: string;
  insuranceFile: string;
  // the DOT inspection document (the field predates the DOT relabel)
  inspectionFile: string;
  liabilityInsuranceFile: string;
  workLog: RentalWorkLogEntry[] | null;
  photoUrl: string;
  updatedAt?: string;
}

// a dated technical-work entry — anything that has been done on the unit,
// with an optional document (invoice, work order)
export type RentalWorkLogEntry = {
  date: string;
  description: string;
  fileUrl: string;
}

export type RentalEquipmentItem = {
  name: string;
  value: string;
}

// a damage mark placed by clicking the trailer diagram; x/y are percentages
// (0-100) of the diagram image, code is an entry of the type's legend
export type RentalDamageMarker = {
  code: string;
  x: number;
  y: number;
}

// a GPS device the trailer dialog can attach; organizationName is set only
// for devices shared from another organization
export type RentalGpsDevice = DeviceWithMapInfo & {
  organizationName?: string;
}

export type RentalWithTrailer = {
  event: RentalTrailerEvent;
  trailer: RentalTrailer | null;
  device: DeviceWithMapInfo | null;
}

// the customer portal's view of a rental lifecycle: its stage, the documents
// of every stage so far, and the link to whatever the customer still has to do
export type CustomerRental = {
  id: number;
  rentalStatus: number;
  createdAt: string;
  updatedAt: string;

  trailerUnitNumber: string;
  make: string;
  model: string;
  year: string;
  trailerType: string;

  rentalStartTime: string | null;
  rentalEndTime: string | null;

  insurancePdfUrl: string;
  insuranceAttachments: RentalInsuranceAttachment[] | null;
  insuranceRejectionReason: string;
  // set only while the customer still has to complete the forms
  insuranceUuid: string;

  agreementPdfUrl: string;
  signedAgreementPdfUrl: string;
  agreementRejectionReason: string;
  // set only while the customer still has to sign
  agreementUuid: string;

  // the signed outbound inspection, once the unit is picked up
  outboundContractPdfUrl: string;
}

export type RentalTire = {
  brand: string;
  value: number;

  serial: string;
  conditionType: string; // "rating" | "depth" | "percentage"
  condition: string;
  comment: string;
}

// brakes are recorded exactly like tires (per wheel position, same condition
// types); serial is unused for brakes and hidden in the UI
export type RentalBrakeInfo = RentalTire;

export type AppliedSignature = {
  signatureUrl: string;
  ip: string;
  timestamp: string;
}

export type AppliedContractInfo = {
  contractPdfUrl: string;
  appliedSignatures: AppliedSignature[] | null;
}

export type RentalGuidedPhotos = {
  front: string;
  rear: string;
  left: string;
  right: string;
};

export type RentalTrailerEvent = {
  id: number;

  createdAt?: string;
  updatedAt?: string;
  eventTime: string;
  active: boolean;
  organizationId: number;

  rentalTrailerEvent: number; // 0 out, 1 in
  rentalTrailerEventStatus: number; // 0 draft, 1 awaiting signature, 2 signed
  // the rental lifecycle this inspection belongs to; 0 on legacy events
  rentalId: number;

  customerId: number;
  customerCompanyName: string;
  customerFirstName: string;
  customerLastName: string;
  customerAddress: Address;
  customerPhone: string;
  customerEmail: string;
  customerComment: string;

  fromCompanyName: string;
  fromFirstName: string;
  fromLastName: string;
  fromAddress: Address;
  fromPhone: string;
  fromEmail: string;

  trailerId: number;
  trailerUnitNumber: string;
  make: string;
  model: string;
  year: string;
  vin: string;
  licensePlateState: string;
  licensePlate: string;
  // snapshot of the trailer's type at event time; stamped server-side on create
  trailerType: string;

  photos: string[] | null;
  // which photo url covers which side of the unit; the urls also appear in photos
  guidedPhotos: RentalGuidedPhotos;
  returnPhotos: string[] | null;
  signature: string;
  signatureCustomer: string;
  prePhotoText: string;
  afterPhotoText: string;
  defects: string[] | null;
  // numbered damage marks on the trailer diagram; codes mirrored into defects
  damageMarkers: RentalDamageMarker[] | null;
  comment: string;

  tires: RentalTire[] | null;
  brakes: RentalBrakeInfo[] | null;

  // usage/equipment fields from the box truck inspection form
  miles: string;
  fuelLevel: string;
  defLevel: string;
  strapsQuantity: string;
  palletJack: string;
  equipment: RentalEquipmentItem[] | null;

  // the rental period, displayed in the organization's time zone; a null end
  // means an open-ended rental
  rentalStartTime: string | null;
  rentalEndTime: string | null;

  // photos of the customer's ID, uploaded on the sign page or by yard staff
  customerIdPhotos: string[] | null;
  confirmedAt: string | null;
  confirmedByUserId: number;
  rejectionReason: string;

  signatureUuid: string;
  appliedContractInfo: AppliedContractInfo;
  signedContractPdfUrl: string;
  contractSentAt: string | null;

  trailerLastUpdated?: string;
}

export type EldDriver = {
  id: number;

  createdAt?: string;
  active: boolean;
  suspended: boolean;
  organizationId: number;

  passwordChangedAt?: string;

  username: string;
  email: string;

  externalId: string;

  newPassword: string;
  newPasswordConfirm: string;

  firstName: string;
  lastName: string;
  driverNumber: string;
  phone: string;
  driverLicenseNumber: string;
  driverLicenseState: string;

  eldVehicleId: number;
  eldVehicleUnitNumber: string;
  eldVehicleVin: string;

  hasAllVehicles: boolean;
  eldVehicleIds: number[];

  trailers: string[];

  exemptDriver: boolean;
  hosRule: string;
  cargoType: string;
  restBreak: '30min' | 'none';
  allowPersonalUse: boolean;
  allowYardMoves: boolean;
  canUseShortSbFirst: boolean;
  unlimitedTrailers: boolean;
  unlimitedShippingDocs: boolean;
  readValuesDirectly: boolean;
  signatureUrl: string;
  loadHistEvents: boolean;
  loadUnidentifiedEvents: boolean;
};

export type EldEngineImmobilizer = {
  id: number,

  organizationId: number,
  deviceId: number,

  simId: string,

  dateActivated: string,

  battery: string,
  gpsFixed: boolean,
  relayOn: boolean,
  powerConnected: boolean,

  speed: number,
  posLat: string,
  posLong: string,

  whereLoadedAt?: string,
  statusLoadedAt?: string,
  whereRequestedAt?: string,
}

export type EldVehicle = {
  id: number;

  createdAt?: string;
  active: boolean;
  organizationId: number;

  apiEnabled: boolean;

  vehicleUnitNumber: string;
  vehicleMake: string;
  vehicleModel: string;
  vehicleYear: string;
  vin: string;
  eldConnectionInterface: string;
  licensePlateState: string;
  licensePlate: string;
  mac: string;

  deviceId: number;

  engineHours: number;
  totalMiles: number;

  odometerOffset: number;
  engineHourOffset: number;

  moving: boolean;
  movingLastUpdated?: string;

  simBasedEldType: number;
};

export type DtcCodeParsed = {
  rawCode: string;
  spn: number;
  spnDesc: string;
  // absent when the SPN was recovered from vendor-decoded text (no FMI/OC in the source)
  fmi?: number;
  fmiDesc?: string;
  occurrence?: number;
  format: string;
};

export type EldVehicleStats = {
  id: number;

  createdAt?: string;
  organizationId: number;
  eldVehicleId: number;

  lastUpdated: string;

  coolantLevelPercent: number,
  coolantTemperature: number,
  fuelLevelPercent: number,
  oilLevelPercent: number,
  oilTemperature: number,
  dtcCodes: string[],
  dtcCodesParsed?: DtcCodeParsed[],
}

export type DtcCodeParsedOBD = {
  code: string;
  description: string;
  format: 'OBD-II';
}

export type DtcCodeParsedJ1939 = {
  rawCode: string;
  spn: number;
  spnDesc: string;
  // absent when the SPN was recovered from vendor-decoded text (no FMI/OC in the source)
  fmi?: number;
  fmiDesc?: string;
  occurrence?: number;
  format: 'J1939';
}

export type DtcCodeParsedJ1708 = {
  rawCode: string;
  pid: number;
  pidDesc: string;
  fmi: number;
  fmiDesc: string;
  occurrence: number;
  format: 'J1708';
}

export type DtcCodeParsedUnknown = {
  rawCode: string;
  description: string;
  format: 'Unknown';
}

export type DtcCodeParsedHistory = DtcCodeParsedOBD | DtcCodeParsedJ1939 | DtcCodeParsedJ1708 | DtcCodeParsedUnknown;

export type EldVehicleDTCHistory = {
  organizationId: number;
  eldVehicleId: number;
  vin: string;
  vehicleUnitNumber: string;
  dtcCode: string;
  lastOnAt: string;
  lastOffAt: string;
  checkEngineLight?: boolean | null;
  dtcCodesParsed: DtcCodeParsedHistory;
}

export type EldUser = {
  id: number;
  name: string;
  email: string;
  newPassword: string;
  newPasswordConfirm: string;
  isDispatcher: boolean;
  updateEldDrivers: boolean;
  updateEldVehicles: boolean;
}

export type EldInspection = {
  id: number;
  organizationId: number;

  eldDriverId: number;
  eldDriverUsername: string;
  eldDriverFirstName: string;
  eldDriverLastName: string;

  inspectionTime: string;
  inspectionType: "FMCSA Email" | "FMCSA API" | "Personal Email" | "Inspected on Device" | "Inspected on Website";

  logFileUrl?: string;
  userComment?: string;
  attachedFileUrl?: string;

  fromEventDate: string;
  toEventDate: string;

  vehicleUnitNumber: string;
  vehicleVin: string;
  vehicleId: number;

  posLat: string;
  posLong: string;
  cityName?: string;

  fmcsaComment: string;
  personalEmail: string;
}

export type EldDevice = {
  id: number;

  createdAt?: string;
  organizationId: number;

  iccid: string;
  active: boolean;
  deviceType: string;
  identifier: string;

  lastVinReported: string;
  useGpsSpeed: boolean;
};

export type EldDvir = {
  id: string;

  createdAt?: string;
  organizationId: number;

  dvirDate: string;
  dvirStatus: string;

  eldDriverId: number;
  eldDriverFirstName: string;
  eldDriverLastName: string;

  vehicleId: number;
  vehicleUnitNumber: string;
  vehicleVin: string;

  vehicleDefects: string[];
  trailers: string[];
  trailerDefects: string[];
  signatureFileUrl: string;

  dvirComment: string;

  formJson: DvirFormField[];
  submissionJson: DvirFormAnswer[];
  flaggedIssues: string[];
  eldEventLineId: string;
  eventDate: string | null;
};

export type EldEventLine = {
  id: string;
  organizationId: number;
  locked: boolean;
  updatedAt?: null | string;

  eventTime: string;
  eventDate: string;
  certificationForDate?: null | string;

  eventType:
  | 'Off Duty'
  | 'Sleeper'
  | 'Driving'
  | 'On Duty'
  | 'Intermediate w/ CLP'
  | 'Intermediate w/ RLP'
  | 'Personal Use'
  | 'Yard Moves'
  | 'PC/YM Cleared'
  | 'Certification'
  | 'Login'
  | 'Logout'
  | 'Engine Power-up w/ CLP'
  | 'Engine Power-up w/ RLP'
  | 'Engine Shut-down w/ CLP'
  | 'Engine Shut-down w/ RLP'
  | 'Malfunction'
  | 'Malfunction cleared'
  | 'Diagnostic'
  | 'Diagnostic Cleared';

  malfunctionCode: '' | 'P' | 'E' | 'T' | 'L' | 'R' | 'S' | 'O' | '1' | '2' | '3' | '4' | '5' | '6';

  numberId: number;
  engineHours: number;
  origin: 'Auto' | 'Driver' | 'Other User' | 'Without Origin' | 'Unidentified Driver';
  vehicleUnitNumber: string;
  vin: string;
  eldDeviceSerialNumber: string;
  eldDeviceSn: string;
  eldDriverId: number;
  odometer: number;
  eventLineStatus:
  | 'Active'
  | 'Inactive Changed'
  | 'Inactive Change Requested'
  | 'Inactive Change Rejected';

  changeRequestedByUserId: number;
  changedEldEventLineId: string;

  latLoc: string;
  longLoc: string;
  locationSource: 'Automatic' | 'Manual' | 'None';
  location: string;
  notes: string;
};

export type RentalOrganizationSettings = {
  id: number;
  organizationId: number;

  companyName: string;
  companyAddress: Address;
  companyLogoUrl: string;
  preImageText: string;
  postImageText: string;

  organizationNameShort: string;
  contactPhone: string;
  contactEmail: string;
  // printed name and signature image stamped into the lessor block of agreements
  ownerName: string;
  ownerSignatureFile: string;
  companyTimeZone: string;
  tireConditionType: string; // "rating" | "depth" | "percentage"
  requirePhotos: boolean;
  requireTireBrand: boolean;
  requireTireSerial: boolean;
  requireTireCondition: boolean;
  requireBrakes: boolean;
  agreementFile: string;
  // TipTap JSON document (stringified); '' = not configured
  agreementTemplate: string;
  // per-trailer-type overrides; an empty field falls back to the defaults above
  typeAgreements: RentalTypeAgreement[] | null;
  // org-authored insurance request form (TipTap JSON, stringified)
  insuranceFormTemplate: string;
  // reusable blank PDFs pre-listed on every insurance request
  blankForms: RentalBlankForm[] | null;
  // extra agreements selectable when sending; none selected = per-type/default
  namedAgreements: RentalNamedAgreement[] | null;
};

export type RentalTypeAgreement = {
  trailerType: string;
  agreementFile: string;
  agreementTemplate: string;
};

export type RentalBlankForm = {
  name: string;
  fileUrl: string;
};

export type RentalNamedAgreement = {
  name: string;
  agreementFile: string;
  agreementTemplate: string;
};

// a blank PDF the customer must populate and upload back; returnedUrl is set
// only server-side by the public upload endpoint
export type RentalInsuranceAttachment = {
  name: string;
  blankUrl: string;
  returnedUrl: string;
  returnedAt: string | null;
};

// the rental lifecycle: insurance request -> agreement e-sign -> outbound -> return
export type Rental = {
  id: number;

  createdAt?: string;
  updatedAt?: string;
  active: boolean;
  organizationId: number;
  rentalStatus: number;

  customerId: number;
  customerCompanyName: string;
  customerFirstName: string;
  customerLastName: string;
  customerAddress: Address;
  customerPhone: string;
  customerEmail: string;

  trailerId: number;
  trailerUnitNumber: string;
  make: string;
  model: string;
  year: string;
  vin: string;
  licensePlateState: string;
  licensePlate: string;
  trailerType: string;

  trailerValue: string;
  // which bank/insurer handles the insurance; filled by the customer
  insuranceBank: string;
  pdOnCustomerInsurance: boolean;
  pdFile: string;
  insuranceAttachments: RentalInsuranceAttachment[] | null;
  insuranceUuid: string;
  insurancePdfUrl: string;
  insuranceSentAt: string | null;
  insuranceSubmittedAt: string | null;
  insuranceConfirmedAt: string | null;
  insuranceConfirmedByUserId: number;
  insuranceRejectionReason: string;

  // agreement fields are populated by staff before sending — the customer
  // only reads and signs
  agreementName: string; // '' = the default (per-type) agreement
  leasePayment: string; // $ per week
  securityDeposit: string;
  equipmentPerMonth: string;
  numberOfPayments: string;
  // choice node id -> the position of the selected option
  choiceSelections: Record<string, string> | null;
  // custom variable name -> the value staff filled in before sending
  customValues: Record<string, string> | null;
  rentalStartTime: string | null;
  rentalEndTime: string | null;

  agreementUuid: string;
  agreementContractInfo: AppliedContractInfo;
  signedAgreementPdfUrl: string;
  agreementSignature: string;
  agreementIdPhotos: string[] | null;
  agreementSentAt: string | null;
  agreementSubmittedAt: string | null;
  agreementConfirmedAt: string | null;
  agreementConfirmedByUserId: number;
  agreementRejectionReason: string;

  outEventId: number;
  inEventId: number;
};

export type EldOrganizationSettings = {
  id: number;
  organizationId: number;
  companyName: string;
  dotNumber: string;
  companyTimeZone: string;
  companyAddress: Address;

  vehicleMotionThreshold: string;
  cycleRule: string;
  cargoType: string;
  restBreak: '30 min' | 'none';
  shortHaulException: boolean;
  personalConveyance: boolean;
  yardMoves: boolean;
  exemptDriver: boolean;
  apiToken: string;

  keepShippingDocs: boolean;
  keepCoDrivers: boolean;

  inspectionNotificationEmails: string[];

  forceDvirForm: boolean;
  dvirFormFields: DvirFormField[];
  dvirNotificationEmails: string[];
};

export type DvirFormField = {
  label: string;
  requirePicture: boolean;
  category: 'Vehicle' | 'Trailer';
};

export type DvirFormAnswer = {
  label: string;
  answer: 'yes' | 'no' | '';
  pictureUrl: string;
};

export type DvirNotification = {
  id: number;

  createdAt: string;
  organizationId: number;

  eldDriverId: number;
  eldDriverFirstName: string;
  eldDriverLastName: string;
  eldVehicleUnitNumber: string;
  eldVehicleVin: string;

  dvirNotificationType: number;

  eventDate: string;
  eldDvirId: string;
};

export type EldDormancySettings = {
  id: number;
  organizationId: number;
  reportDormancy: boolean;
  dormancyDurationDays: number,
  emails: string[]
}

export type EventStatusType = 'Driving' | 'Sleeper' | 'Off Duty' | 'On Duty' | "Yard Moves" | "Personal Use";


export type EldDriverLogGroup = {
  eldDriverId: number;
  username: string;
  firstName: string;
  lastName: string;
  suspended: boolean;
  lastStatus: EventStatusType;
  lastLocation: string;
  vehicleUnitNumber: string;

  breakTime: number;
  driveTime: number;
  shiftTime: number;
  cycleTime: number;
  violations: string[];

  eldSerialNumber: string;
  lastConnectionStatus: string;
  eldSn: string;
  eldVehicleId: number;

  updatedAt: string;
  lastConnected?: string;
};

export type EldDailyRecord = {
  id: string;

  createdAt?: string;
  updatedAt?: string;
  organizationId: number;

  lastRegrind?: string;
  eldStatusEventCount: number;
  lastStatusEvent: EventStatusType;
  lastLocation: string;

  dailyDate: string;
  isCertified: boolean;

  eldDriverId: number;
  eldDriverUsername: string;
  eldDriverFirstName: string;
  eldDriverLastName: string;

  eldCoDriverId: number;
  eldCoDriverUsername: string;
  eldCoDriverFirstName: string;
  eldCoDriverLastName: string;

  vehicleId: number;
  vehicleUnitNumber: string;
  vehicleVin: string;

  trailers: string[];
  shippingDocs: string[];

  totalHoursDriving: number;
  totalHoursOnDuty: number;
  violations: string[];

  breakTime: number;
  driveTime: number;
  shiftTime: number;
  cycleTime: number;
};



export type LatLng = [number, number];

export type Polygon = LatLng[]

export type Circle = {
  center: LatLng,
  radius: number
}

export type EldLandmark = {
  id: number
  createdAt?: string,
  deletedAt?: string,
  active: boolean,
  organizationId: number,
  name: string,
  landmarkType: string,
  latLoc: number,
  longLoc: number,
  reportDormancy: boolean;
  dormancyDurationDays: number;
  mainAddress: Address,
  appliesToAll: boolean,
  deviceIds: number[],
  polygons: Polygon[],
  circles: Circle[],
  emails: string[],
  notifyType: number, // 0 coming in, 1 going out, 2 all
  appliesToDeviceType: number[], // -2 all, 0 trucks, 1 ct trailers, 2 samsara trailers, 3 skybitz trailers, 4 custom
  appliesToOrganizationIds: number[], // empty all, or specific ids
  appliesToDeviceIds: number[], // device ids this applies to in case deviceType is 6
}

export type EldAssetNotification = {
  id: number,
  organizationId: number,
  deviceOrganizationId: number,
  deviceType: string,
  deviceId: number,
  eldAssetNotificationType: number,
  device: Device,
  notificationTime: string,
  eldLandmark: EldLandmark,
  notes: string,
}


export type ScheduledReport = {
  id: number,
  createdAt?: string,
  organizationId: number,
  scheduledReportType: number,
  emails: string[],
  request: string,
  sendingInterval: number,
  lastSent?: string,
  nextSend?: string,
  sendAfter: string,
}


export type Shape = { type: "Circle", center: L.LatLng, radius: number } | { type: "Polygon", coordinates: L.LatLngTuple[] };

// Safety Trips
export type SafetyTrip = {
  deviceId: number;
  organizationId: number;
  safetyTripId: string; // uuid
  eventType: number; // 0 = TRIP_START, 1 = TRIP_END
  eventTime: string;
  posLat: number;
  posLong: number;
  odometer?: number;
  velocity?: number;
  location: string;
  totalMiles?: number; // only set if eventType is TRIP_END (1)
  totalScore?: number; // only set if eventType is TRIP_END (1)
};

export type SafetyTripFull = {
  start: SafetyTrip;
  end?: SafetyTrip;
  tripImageUrl: string;
  deviceHistoryLines: DeviceHistoryLine[];
};


export type DeviceHistoryLine = {
  recordedAt: string;
  posLat: string;
  posLong: string;
  odometer?: number;
  engineHours?: number;
  fuelLevel?: number;
  battery?: number;
  direction?: number;
  velocity?: number;
  lineOrigin?: number;
}